import styles from '@/pages/app/Settings/styles.module.scss'

import InfoIcon from '@/assets/icons/info.svg?react'
import { Dropdown, DropdownItem } from '@/components/Dropdown'
import Tooltip from '@/components/Tooltip'
import { useHasDepartments } from '@/hooks/useHasDepartments'
import PermissionTooltip from '@/pages/app/Settings/PermissionTooltip'
import { OrgInsightsMode } from '@/types'

// Keep helperText short: Dropdown menus size to `width: max-content`, so long copy widens them.
const OPTIONS = [
  {
    id: OrgInsightsMode.DefaultOn,
    title: 'Default visible',
    helperText: 'New meetings start on. Members can turn any meeting off.',
  },
  {
    id: OrgInsightsMode.DefaultOff,
    title: 'Default not visible',
    helperText: 'New meetings start off. Members can turn any meeting on.',
  },
  {
    id: OrgInsightsMode.Individual,
    title: 'Managed individually',
    helperText: 'Each member picks their own default, which starts on.',
  },
  {
    id: OrgInsightsMode.Disabled,
    title: 'Disabled',
    helperText: 'No meetings are used for insights.',
  },
]

const getDropdownItems = (current: OrgInsightsMode): DropdownItem[] =>
  OPTIONS.map((option) => ({ ...option, selected: option.id === current }))

type Props = {
  mode: OrgInsightsMode
  isAdmin: boolean
  isSaving: boolean
  onSelect: (mode: OrgInsightsMode) => void
}

/** Whether members' meetings may feed the org's AI insights, and what a new meeting starts as. */
export default function OrgInsightsSection({ mode, isAdmin, isSaving, onSelect }: Props) {
  const noun = useHasDepartments() ? 'organization' : 'team'

  const handleSelect = (selectedItems: DropdownItem[]) => {
    if (selectedItems.length === 0) return
    onSelect(selectedItems[0].id as OrgInsightsMode)
  }

  return (
    <div className={styles.settingsItem} data-testid="org-insights-setting">
      <div className={styles.toggleLabel}>
        Org insights
        <Tooltip
          text={`What new meetings start as for your ${noun}'s AI insights. Members can switch any meeting on or off unless this is disabled.`}
          placement="top"
          hideArrow
        >
          <InfoIcon className={styles.infoIcon} />
        </Tooltip>
      </div>
      <PermissionTooltip>
        <Dropdown
          items={getDropdownItems(mode)}
          onSelect={handleSelect}
          placeholder="Select default"
          className={styles.localDataPolicyDropdown}
          disabled={!isAdmin || isSaving}
          ariaLabel="Org insights"
        />
      </PermissionTooltip>
    </div>
  )
}
